/**
 * Shared ApexCharts brand theme.
 *
 * There was no shared chart config before this file — the one chart in the app
 * carried its options inline, which is how it ended up with `endingShape:
 * "rounded"`, an option ApexCharts removed in v3.24. The installed version is
 * 4.4.0, so those bars have silently been square. The bar preset below uses the
 * modern `borderRadius` + `borderRadiusApplication` pair instead.
 *
 * Import from every chart so the palette and grid stay consistent as more get
 * added.
 */

export const CHART_COLORS = {
    brand: "#0026FF",
    glow: "#3355FF",
    violet: "#8B5CF6",
    navy: "#1A1A8C",
    teal: "#0E9F6E",
    ink: "#0B1220",
    muted: "#7F8A9F",
    grid: "#E6E8F0",
};

/**
 * Categorical series order: brand first, then hues chosen for maximum
 * separation rather than for being "more blues". Cap at six — beyond that,
 * group the tail into an "Other" series; no palette stays distinguishable past
 * about six categories.
 */
export const CHART_SERIES = [
    CHART_COLORS.brand,
    CHART_COLORS.violet,
    CHART_COLORS.teal,
    CHART_COLORS.navy,
    "#F2A93B",
    CHART_COLORS.muted,
];

const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";

/** Honour the OS reduced-motion setting rather than animating unconditionally. */
const prefersReducedMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const baseChartOptions = {
    chart: {
        fontFamily: FONT,
        foreColor: CHART_COLORS.muted,
        toolbar: { show: false },
        zoom: { enabled: false },
        dropShadow: { enabled: false },
        animations: {
            enabled: !prefersReducedMotion,
            easing: "easeinout",
            speed: 420,
            animateGradually: { enabled: true, delay: 60 },
        },
    },
    colors: CHART_SERIES,
    dataLabels: { enabled: false },
    grid: {
        borderColor: CHART_COLORS.grid,
        // Dashed horizontals only. A full solid grid competes with the data;
        // vertical lines are redundant when the x-axis is categorical.
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: -6, right: 8, bottom: 0, left: 8 },
    },
    xaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
            style: { fontFamily: FONT, fontSize: "11px", fontWeight: 500, colors: CHART_COLORS.muted },
        },
        crosshairs: {
            stroke: { color: "rgba(0,38,255,.20)", width: 1, dashArray: 3 },
        },
        tooltip: { enabled: false },
    },
    yaxis: {
        labels: {
            style: { fontFamily: FONT, fontSize: "11px", fontWeight: 500, colors: CHART_COLORS.muted },
            formatter: (v) => (typeof v === "number" ? v.toLocaleString() : v),
        },
    },
    legend: {
        position: "top",
        horizontalAlign: "right",
        offsetY: -4,
        fontFamily: FONT,
        fontSize: "12px",
        fontWeight: 500,
        labels: { colors: CHART_COLORS.ink },
        markers: { width: 8, height: 8, radius: 8, offsetX: -3 },
        itemMargin: { horizontal: 10 },
    },
    tooltip: {
        theme: "light",
        style: { fontSize: "12px", fontFamily: FONT },
        marker: { show: true },
    },
    states: {
        hover: { filter: { type: "lighten", value: 0.06 } },
        active: { filter: { type: "darken", value: 0.04 } },
    },
    // Without this an empty result renders as a bare axis with no explanation.
    noData: {
        text: "No data for this period",
        style: { color: CHART_COLORS.muted, fontSize: "13px", fontFamily: FONT },
    },
};

/** Shallow-recursive merge so callers can override any nested option. */
function deepMerge(base, override) {
    const out = { ...base };
    for (const key of Object.keys(override)) {
        const val = override[key];
        out[key] =
            val && typeof val === "object" && !Array.isArray(val)
                ? deepMerge(base[key] || {}, val)
                : val;
    }
    return out;
}

export const barChartOptions = (overrides = {}) =>
    deepMerge(
        {
            ...baseChartOptions,
            chart: { ...baseChartOptions.chart, type: "bar", stacked: false },
            plotOptions: {
                bar: {
                    columnWidth: "38%",
                    // Replaces the removed `endingShape: "rounded"`.
                    borderRadius: 6,
                    borderRadiusApplication: "end",
                    borderRadiusWhenStacked: "last",
                },
            },
            // Transparent stroke is how ApexCharts spaces grouped bars.
            stroke: { show: true, width: 3, colors: ["transparent"] },
            fill: {
                type: "gradient",
                gradient: {
                    shade: "light",
                    type: "vertical",
                    shadeIntensity: 0.18,
                    opacityFrom: 1,
                    opacityTo: 0.74,
                    stops: [0, 100],
                },
            },
        },
        overrides
    );

export const areaChartOptions = (overrides = {}) =>
    deepMerge(
        {
            ...baseChartOptions,
            chart: { ...baseChartOptions.chart, type: "area" },
            stroke: { curve: "smooth", width: 2.5, lineCap: "round" },
            markers: { size: 0, hover: { size: 5, sizeOffset: 2 } },
            fill: {
                type: "gradient",
                gradient: { shadeIntensity: 1, opacityFrom: 0.28, opacityTo: 0.02, stops: [0, 92, 100] },
            },
        },
        overrides
    );

/** Sparkline preset for inline trends inside KPI tiles. */
export const sparkOptions = (color = CHART_COLORS.brand) => ({
    chart: { type: "area", sparkline: { enabled: true }, animations: { enabled: false } },
    colors: [color],
    stroke: { curve: "smooth", width: 2 },
    fill: { type: "gradient", gradient: { opacityFrom: 0.3, opacityTo: 0 } },
    tooltip: { enabled: false },
});
